import { SPOT_MAX_H, SPOT_MIN_H } from "./collectables";
import { GAP_KINDS, type Gap } from "./gaps";
import type { MapHub } from "./hubs";
import { HillRoad } from "./hill-road";
import { EDGE_FAR, type EdgeSide, type Level, type SoftEdge } from "./level";
import { checkLadder, MAX_RUNG_POINTS, TIER_NAMES, type MapChallenge } from "./ladder";
import { Checker, COLOR, type Obj } from "./mapcheck";
import type { MapLook, Mood, Outside } from "./maps";
import { DEFAULT_GOALS, goalIds, type MapGoals } from "./run";
import { pieceKind, PIECES, type MapPiece } from "./prims";
import { checkTask, TASK_KINDS, type TaskKind } from "./tasks";
import { rad, type Compiled, type PieceReach } from "./prims/types";
import { SIGN_TEXT } from "./prims/scenery";
import { HILL_FINISH } from "./sim/tuning";
import { placeRoad } from "./hill-ramps";
import { buildTag, checkGraffiti, type MapTag } from "./graffiti";
import { checkProps, type MapProp } from "./props";
import { buildTarget, checkTargets, type MapTarget } from "./targets";
import { buildVehicle, checkVehicles, type MapVehicle } from "./vehicles";

export type { MapPiece } from "./prims";
export type { LedgeSide } from "./prims/box";

// Maps as data. A map file is plain JSON: what the park is made of (pieces),
// where you start, and how it looks. `parseMapFile` checks an untrusted file
// (hand-written, or one day generated from a prompt) and says exactly what's
// wrong with it; `buildLevel` turns a checked file into the sim's `Level`.
//
// Angles are in degrees so files read (and get written) easily. A piece's
// `rot` turns it about y: 0 means its ramp rises toward +x (east), 90 toward
// +z (south), -90 toward -z (north), 180 toward -x (west).

export const MAP_FILE_VERSION = 1;

export interface MapFile {
  version: typeof MAP_FILE_VERSION;
  id: string;
  name: string;
  blurb: string;
  /** Half-size of the square park; the walls sit at ±bounds. */
  bounds: number;
  /**
   * A rectangular park instead (#115): the walls' half-size along x (east
   * to west) and z (north to south), neither more than `bounds`. Left out,
   * the park is square.
   */
  extent?: { x: number; z: number };
  /** Where you start; `yaw` in degrees (0 faces +z, 180 faces -z). */
  spawn: { x: number; z: number; yaw: number };
  look: MapLook;
  /** In order: where two pieces are the same height, the earlier one wins. */
  pieces: MapPiece[];
  /** Named gaps (see gaps.ts). Names are unique within a map. */
  gaps?: Gap[];
  /** The 2-minute run's goal list (see run.ts); a default one if left out. */
  goals?: MapGoals;
  /**
   * The challenge ladder (#66): every goal's tier, what it requires and the
   * stat points it pays, by goal id ("score-0", "skate", "gap-Rail Hop"...).
   * Left out, every goal is open on tier 1 and pays a point.
   */
  challenges?: MapChallenge[];
  /**
   * The challenge hubs (#86): one per tier, where you pick that tier's
   * challenges from a list. Left out (a map loaded in dev), every challenge
   * gets its own marker instead.
   */
  hubs?: MapHub[];
  /**
   * Soft boundaries (#82): sides that are a beach instead of a wall. `sand`
   * is how many metres inside the fence line are sand; `water` puts the sea
   * past it. At least one side stays a wall. Left out, all four are walls.
   */
  edges?: SoftEdge[];
  /**
   * Vehicles (#37): each drives a loop on the park's flat floor, round and
   * round, for skitching (see vehicles.ts). `playcheck` checks the loop is clear.
   */
  vehicles?: MapVehicle[];
  /** Paintball targets (#137, targets.ts): things to shoot with the paintball gun. */
  targets?: MapTarget[];
  /** Graffiti tags (#151, graffiti.ts): for the power washer to wash off. */
  graffiti?: MapTag[];
  /** Cones and cans (#151, props.ts): for the power washer to knock about. */
  props?: MapProp[];
  /**
   * Bomb the Hill (#193): the park is a hill falling `grade` metres per
   * metre toward +z, ridden in the hill mode (carve to brake, speed wobbles,
   * distance down it). Left out, the floor is flat.
   */
  hill?: MapHill;
  /**
   * Gone (#86): stat points now come only from hub challenges. Old files
   * with it still load; it's ignored (`mapFileWarnings` says so).
   */
  statPoints?: unknown;
}

export class MapFileError extends Error {
  constructor(readonly problems: string[]) {
    super(`Invalid map file:\n${problems.map((p) => `- ${p}`).join("\n")}`);
    this.name = "MapFileError";
  }
}

const OUTSIDES: readonly Outside[] = ["park", "plaza", "roof", "beach", "street"];
const MOODS: readonly Mood[] = ["noon", "golden", "overcast", "dusk", "tungsten"];
const MIN_BOUNDS = 10;
const MAX_BOUNDS = 200;
const ID = /^[a-z0-9][a-z0-9-]{0,39}$/;

function checkLook(c: Checker, v: unknown, path: string): void {
  const o = c.obj(v, path);
  if (!o) return;
  c.only(
    o,
    ["indoor", "outside", "sky", "skyTop", "fogNear", "fogFar", "hemiSky", "hemiGround", "hemiIntensity", "sunIntensity", "sunTint", "mood", "floor", "walls", "wallHeight", "roof", "pieces", "streetEnds", "shopRows"],
    path,
  );
  if (o.pieces !== undefined) {
    const pieces = c.obj(o.pieces, `${path}.pieces`);
    if (pieces) {
      c.only(pieces, ["block", "ramp", "rail"], `${path}.pieces`);
      for (const k of ["block", "ramp", "rail"]) c.color(pieces, k, `${path}.pieces`);
    }
  }
  if (typeof o.indoor !== "boolean") c.fail(`${path}.indoor`, "expected true or false");
  if (o.outside !== undefined) {
    if (!OUTSIDES.includes(o.outside as never)) c.fail(`${path}.outside`, `expected one of ${OUTSIDES.map((k) => `"${k}"`).join(", ")}`);
    else if (o.indoor === true) c.fail(`${path}.outside`, "only for outdoor maps");
  }
  if (o.mood !== undefined && !MOODS.includes(o.mood as never)) c.fail(`${path}.mood`, `expected one of ${MOODS.map((k) => `"${k}"`).join(", ")}`);
  for (const k of ["sky", "hemiSky", "hemiGround", "floor"]) c.color(o, k, path);
  for (const k of ["skyTop", "sunTint"]) if (o[k] !== undefined) c.color(o, k, path);
  const near = c.num(o, "fogNear", path, { min: 0 });
  const far = c.num(o, "fogFar", path, { min: 0 });
  if (far <= near) c.fail(`${path}.fogFar`, "must be beyond fogNear");
  c.num(o, "hemiIntensity", path, { min: 0, max: 10 });
  c.num(o, "sunIntensity", path, { min: 0, max: 10 });
  const wallHeight = c.num(o, "wallHeight", path, { min: 0.5, max: 50 });
  if (!Array.isArray(o.walls) || o.walls.length !== 4 || !o.walls.every((w) => typeof w === "string" && COLOR.test(w)))
    c.fail(`${path}.walls`, "expected four colors like \"#aabbcc\" (south, east, north, west)");
  if (o.roof !== null) {
    const roof = c.num(o, "roof", path, { min: 2, max: 50 });
    if (roof < wallHeight) c.fail(`${path}.roof`, "must be at least wallHeight");
  }
  if (o.indoor === true && o.roof === null) c.fail(`${path}.roof`, "an indoor map needs a roof height");
}

const MAX_GAP_POINTS = 5000;

/** A gap's zone: as far out as a piece may stand (#244: a pier's benches), so a gap can name anything you can skate. */
function checkZone(c: Checker, v: unknown, path: string, bounds: number, reach: PieceReach): void {
  const o = c.obj(v, path);
  if (!o) return;
  c.only(o, ["x", "z", "hx", "hz"], path);
  c.num(o, "x", path, reach.x);
  c.num(o, "z", path, reach.z);
  c.num(o, "hx", path, { min: 0.1, max: bounds });
  c.num(o, "hz", path, { min: 0.1, max: bounds });
}

function checkGaps(c: Checker, v: unknown, path: string, bounds: number, reach: PieceReach): void {
  if (v === undefined) return;
  if (!Array.isArray(v)) {
    c.fail(path, "expected a list");
    return;
  }
  const names = new Set<string>();
  v.forEach((g, i) => {
    const p = `${path}[${i}]`;
    const o = c.obj(g, p);
    if (!o) return;
    c.only(o, ["name", "points", "kind", "from", "to"], p);
    const name = c.str(o, "name", p);
    if (names.has(name)) c.fail(`${p}.name`, `"${name}" is already a gap on this map`);
    names.add(name);
    c.num(o, "points", p, { min: 1, max: MAX_GAP_POINTS });
    if (!GAP_KINDS.includes(o.kind as Gap["kind"])) c.fail(`${p}.kind`, `expected one of ${GAP_KINDS.join(", ")}`);
    checkZone(c, o.from, `${p}.from`, bounds, reach);
    checkZone(c, o.to, `${p}.to`, bounds, reach);
  });
}

const MAX_GOAL_SCORE = 10_000_000;
const MAX_GAP_GOALS = 3;

/** A pickup's spot: inside the fence, and floating `h` above the surface where it can be reached. */
function checkSpot(c: Checker, v: unknown, path: string, bounds: number): void {
  const o = c.obj(v, path);
  if (!o) return;
  c.only(o, ["x", "z", "h"], path);
  const inside = { min: -bounds + 1, max: bounds - 1 };
  c.num(o, "x", path, inside);
  c.num(o, "z", path, inside);
  c.num(o, "h", path, { min: SPOT_MIN_H, max: SPOT_MAX_H });
}

function checkLetters(c: Checker, v: unknown, path: string, bounds: number): void {
  if (v === undefined) return;
  if (!Array.isArray(v) || v.length !== 5) {
    c.fail(path, "expected five spots, one per letter in order");
    return;
  }
  v.forEach((s, i) => checkSpot(c, s, `${path}[${i}]`, bounds));
}

function checkGoals(c: Checker, v: unknown, path: string, gaps: unknown, bounds: number): void {
  if (v === undefined) return;
  const o = c.obj(v, path);
  if (!o) return;
  c.only(o, ["scores", "gaps", "letters", "combo", "tape"], path);
  const s = o.scores;
  if (
    !Array.isArray(s) ||
    s.length !== 3 ||
    !s.every((n, i) => typeof n === "number" && Number.isInteger(n) && n > 0 && n <= MAX_GOAL_SCORE && (i === 0 || n > (s[i - 1] as number)))
  )
    c.fail(`${path}.scores`, "expected three rising whole numbers (High, Pro, Sick score)");
  if (!Array.isArray(o.gaps) || o.gaps.length > MAX_GAP_GOALS) {
    c.fail(`${path}.gaps`, `expected a list of up to ${MAX_GAP_GOALS} gap names`);
  } else {
    const names = new Set(Array.isArray(gaps) ? gaps.map((g) => (g as { name?: unknown })?.name) : []);
    o.gaps.forEach((name, i) => {
      if (typeof name !== "string" || !names.has(name)) c.fail(`${path}.gaps[${i}]`, "expected the name of one of this map's gaps");
    });
  }
  checkLetters(c, o.letters, `${path}.letters`, bounds);
  checkLetters(c, o.combo, `${path}.combo`, bounds);
  if (o.tape !== undefined) checkSpot(c, o.tape, `${path}.tape`, bounds);
}

/**
 * The `challenges` list: one entry per goal the map has, plus any tasks (an
 * entry with a `kind`, see tasks.ts) under ids of their own; tiers 1-3, 0-2
 * stat points, and requirements that are on the list, on the same tier or
 * lower, and open up (no cycles).
 */
function checkChallenges(c: Checker, v: unknown, path: string, goals: unknown, targets: number, tags: number): void {
  if (!Array.isArray(v)) {
    c.fail(path, "expected a list of challenges");
    return;
  }
  const mapGoals = (goals ?? DEFAULT_GOALS) as MapGoals;
  const ids = goalIds(mapGoals);
  const known = new Set(ids);
  const gapNames = mapGoals.gaps;
  const tiers = new Map<string, number>();
  v.forEach((e, i) => {
    const o = c.obj(e, `${path}[${i}]`);
    if (!o) return;
    const at = `${path}[${i}]`;
    const task = o.kind !== undefined;
    const kind = task && Object.hasOwn(TASK_KINDS, o.kind as string) ? TASK_KINDS[o.kind as TaskKind] : null;
    c.only(o, ["id", "tier", "requires", "statPoints", ...(task ? ["kind", ...(kind?.fields ?? [])] : [])], at);
    const id = c.str(o, "id", at, task ? ID : undefined);
    if (task) {
      if (id && known.has(id)) c.fail(`${at}.id`, `"${id}" is a goal's id: a task needs its own`);
      else if (id && /^(score-|gap-)/.test(id)) c.fail(`${at}.id`, "task ids can't start with score- or gap-");
      checkTask(c, o, at, { gaps: gapNames, targets, tags });
    } else if (id && !known.has(id))
      c.fail(`${at}.id`, `"${id}" isn't one of this map's goals (${ids.join(", ")}); a task needs a \`kind\``);
    if (id && tiers.has(id)) c.fail(`${at}.id`, `"${id}" is listed twice`);
    const tier = c.num(o, "tier", at, { min: 1, max: TIER_NAMES.length });
    if (!Number.isInteger(tier)) c.fail(`${at}.tier`, "expected a whole number");
    const points = c.num(o, "statPoints", at, { min: 0, max: MAX_RUNG_POINTS });
    if (!Number.isInteger(points)) c.fail(`${at}.statPoints`, "expected a whole number");
    if (o.requires !== undefined && (!Array.isArray(o.requires) || !o.requires.every((r) => typeof r === "string")))
      c.fail(`${at}.requires`, "expected a list of goal ids");
    if (id) tiers.set(id, tier);
  });
  if (c.problems.some((p) => p.startsWith(path))) return;
  const list = v as MapChallenge[];
  for (const id of ids) if (!tiers.has(id)) c.fail(path, `"${id}" is missing: every goal needs a place on the ladder`);
  list.forEach((e, i) => {
    for (const need of e.requires ?? []) {
      if (need === e.id) c.fail(`${path}[${i}].requires`, "can't require itself");
      else if ((tiers.get(need) ?? 0) > e.tier) c.fail(`${path}[${i}].requires`, `"${need}" is on a higher tier`);
    }
  });
  for (const problem of checkLadder(list.map((e) => ({ ...e, requires: e.requires ?? [] })))) c.fail(path, problem);
}

/** The `hubs` list: one hub per tier, inside the fence (where it stands on the ground is `playcheck`'s job). */
function checkHubs(c: Checker, v: unknown, path: string, bounds: number): void {
  if (!Array.isArray(v) || v.length !== TIER_NAMES.length) {
    c.fail(path, `expected a list of ${TIER_NAMES.length} hubs, one per tier`);
    return;
  }
  const seen = new Set<number>();
  v.forEach((e, i) => {
    const at = `${path}[${i}]`;
    const o = c.obj(e, at);
    if (!o) return;
    c.only(o, ["tier", "x", "z", "yaw"], at);
    const tier = c.num(o, "tier", at, { min: 1, max: TIER_NAMES.length });
    if (!Number.isInteger(tier)) c.fail(`${at}.tier`, "expected a whole number");
    else if (seen.has(tier)) c.fail(`${at}.tier`, `tier ${tier} has two hubs`);
    seen.add(tier);
    const inside = { min: -bounds + 1, max: bounds - 1 };
    c.num(o, "x", at, inside);
    c.num(o, "z", at, inside);
    c.num(o, "yaw", at, { min: -360, max: 360 });
  });
}

const EDGE_SIDES: readonly EdgeSide[] = ["north", "south", "east", "west"];
/** Sand strip widths a map may use (m). */
export const SAND_MIN = 2;
export const SAND_MAX = 12;

/** The `edges` list (#82): each a different side, a sane sand strip, and at least one wall left. */
function checkEdges(c: Checker, v: unknown, path: string, bounds: number): void {
  if (!Array.isArray(v) || v.length > EDGE_SIDES.length - 1) {
    c.fail(path, `expected a list of at most ${EDGE_SIDES.length - 1} soft sides (at least one side stays a wall)`);
    return;
  }
  const seen = new Set<unknown>();
  v.forEach((e, i) => {
    const at = `${path}[${i}]`;
    const o = c.obj(e, at);
    if (!o) return;
    c.only(o, ["side", "sand", "water"], at);
    if (!EDGE_SIDES.includes(o.side as EdgeSide)) c.fail(`${at}.side`, `expected one of ${EDGE_SIDES.join(", ")}`);
    else if (seen.has(o.side)) c.fail(`${at}.side`, `${String(o.side)} is listed twice`);
    seen.add(o.side);
    c.num(o, "sand", at, { min: SAND_MIN, max: Math.min(SAND_MAX, bounds / 2) });
    if (typeof o.water !== "boolean") c.fail(`${at}.water`, "expected true or false");
  });
}

/** How tall a street end's street may stand (m): about a two-storey deck. */
export const STREET_END_MAX_Y = 6;
/** The narrowest street end (m): room for a road and its sidewalks. */
export const STREET_END_MIN = 8;

/**
 * `look.streetEnds` (#236): each on a wall side (not a beach or a street
 * map's building fronts), at least `STREET_END_MIN` wide, inside that side's
 * length, and not overlapping another on the same side.
 */
function checkStreetEnds(c: Checker, o: Obj, bounds: number): void {
  const look = o.look as Obj;
  const v = look.streetEnds;
  const path = "map.look.streetEnds";
  if (!Array.isArray(v)) {
    c.fail(path, "expected a list");
    return;
  }
  if (look.indoor === true || look.outside === "street") c.fail(path, "only for outdoor maps with walls (not indoor or a street)");
  const extent = o.extent as { x?: number; z?: number } | undefined;
  const soft = Array.isArray(o.edges) ? o.edges.map((e) => (e as { side?: unknown }).side) : [];
  const spans: { side: unknown; from: number; to: number }[] = [];
  v.forEach((e, i) => {
    const at = `${path}[${i}]`;
    const s = c.obj(e, at);
    if (!s) return;
    c.only(s, ["side", "from", "to", "y"], at);
    if (!EDGE_SIDES.includes(s.side as EdgeSide)) c.fail(`${at}.side`, `expected one of ${EDGE_SIDES.join(", ")}`);
    else if (soft.includes(s.side)) c.fail(`${at}.side`, `${String(s.side)} is a beach, not a wall`);
    const half = s.side === "east" || s.side === "west" ? (extent?.z ?? bounds) : (extent?.x ?? bounds);
    const from = c.num(s, "from", at, { min: -half, max: half });
    const to = c.num(s, "to", at, { min: -half, max: half });
    c.num(s, "y", at, { min: 0, max: STREET_END_MAX_Y });
    if (to - from < STREET_END_MIN) c.fail(`${at}.to`, `must be at least ${STREET_END_MIN} m past from`);
    if (spans.some((p) => p.side === s.side && p.from < to && from < p.to)) c.fail(at, `overlaps another street end on the ${String(s.side)} side`);
    spans.push({ side: s.side, from, to });
  });
}

/** The narrowest shop row (m): about one shopfront. */
export const SHOP_ROW_MIN = 6;

/**
 * `look.shopRows` (#240): each on a wall side (not a beach or a street map's
 * building fronts), at least `SHOP_ROW_MIN` wide, inside that side's length,
 * overlapping no other row or street end on the same side, and naming its
 * shops (a sign's words, no name twice on the map).
 */
function checkShopRows(c: Checker, o: Obj, bounds: number): void {
  const look = o.look as Obj;
  const v = look.shopRows;
  const path = "map.look.shopRows";
  if (!Array.isArray(v)) {
    c.fail(path, "expected a list");
    return;
  }
  if (look.indoor === true || look.outside === "street") c.fail(path, "only for outdoor maps with walls (not indoor or a street)");
  const extent = o.extent as { x?: number; z?: number } | undefined;
  const soft = Array.isArray(o.edges) ? o.edges.map((e) => (e as { side?: unknown }).side) : [];
  const ends = Array.isArray(look.streetEnds) ? (look.streetEnds as { side: unknown; from: number; to: number }[]) : [];
  const spans: { side: unknown; from: number; to: number }[] = [];
  const names = new Set<string>();
  v.forEach((e, i) => {
    const at = `${path}[${i}]`;
    const s = c.obj(e, at);
    if (!s) return;
    c.only(s, ["side", "from", "to", "shops"], at);
    if (!EDGE_SIDES.includes(s.side as EdgeSide)) c.fail(`${at}.side`, `expected one of ${EDGE_SIDES.join(", ")}`);
    else if (soft.includes(s.side)) c.fail(`${at}.side`, `${String(s.side)} is a beach, not a wall`);
    const half = s.side === "east" || s.side === "west" ? (extent?.z ?? bounds) : (extent?.x ?? bounds);
    const from = c.num(s, "from", at, { min: -half, max: half });
    const to = c.num(s, "to", at, { min: -half, max: half });
    if (to - from < SHOP_ROW_MIN) c.fail(`${at}.to`, `must be at least ${SHOP_ROW_MIN} m past from`);
    if ([...spans, ...ends].some((p) => p.side === s.side && p.from < to && from < p.to)) c.fail(at, `overlaps another shop row or street end on the ${String(s.side)} side`);
    spans.push({ side: s.side, from, to });
    if (!Array.isArray(s.shops) || s.shops.length === 0) {
      c.fail(`${at}.shops`, "expected a non-empty list of shop names");
      return;
    }
    s.shops.forEach((n, k) => {
      if (typeof n !== "string" || !SIGN_TEXT.test(n)) c.fail(`${at}.shops[${k}]`, `${JSON.stringify(n)} isn't a shop's name (capitals, up to 26 characters)`);
      else if (names.has(n)) c.fail(`${at}.shops[${k}]`, `${n} is already on this map`);
      else names.add(n);
    });
  });
}

/**
 * A rectangular park's `extent` (#115): each half-size between the smallest
 * park and `bounds`, and no beach sides (they're drawn for a square park).
 * Then everything that has to stand inside the walls (the spawn, hubs and
 * pickups) is checked against it; pieces and zones only against `bounds`,
 * like on a square map (playcheck finds a piece you can't get to).
 */
function checkExtent(c: Checker, o: Obj, bounds: number): void {
  const e = c.obj(o.extent, "map.extent");
  if (!e) return;
  c.only(e, ["x", "z"], "map.extent");
  // A hill's road (#194) can be narrower than any park.
  const x = c.num(e, "x", "map.extent", { min: o.hill === undefined ? MIN_BOUNDS : HILL_MIN_HALF_WIDTH, max: bounds });
  const z = c.num(e, "z", "map.extent", { min: MIN_BOUNDS, max: bounds });
  if (o.edges !== undefined) c.fail("map.edges", "a rectangular park (extent) can't have beach sides");
  if (c.problems.length) return;
  const inside = (v: unknown, path: string) => {
    const p = v as { x?: unknown; z?: unknown } | undefined;
    if (typeof p?.x !== "number" || typeof p.z !== "number") return;
    if (Math.abs(p.x) > x - 1) c.fail(`${path}.x`, `expected a number from ${1 - x} to ${x - 1} (inside the extent)`);
    if (Math.abs(p.z) > z - 1) c.fail(`${path}.z`, `expected a number from ${1 - z} to ${z - 1} (inside the extent)`);
  };
  inside(o.spawn, "map.spawn");
  if (Array.isArray(o.hubs)) o.hubs.forEach((h, i) => inside(h, `map.hubs[${i}]`));
  const goals = o.goals as { letters?: unknown; combo?: unknown; tape?: unknown } | undefined;
  for (const k of ["letters", "combo"] as const) {
    const list = goals?.[k];
    if (Array.isArray(list)) list.forEach((s, i) => inside(s, `map.goals.${k}[${i}]`));
  }
  if (goals?.tape !== undefined) inside(goals.tape, "map.goals.tape");
}

/** A map file's hill (#193): its grade, and (#194) the seed its obstacles are laid out from (none without one). */
export interface MapHill {
  grade: number;
  seed?: number;
}

/** A hill's road (#194) is at least this far either side of its middle (m). */
const HILL_MIN_HALF_WIDTH = 4;

/** The steepest and gentlest hill a map can be (#193), metres of fall per metre. */
const HILL_GRADES = { min: 0.02, max: 0.3 };

function checkHill(c: Checker, v: unknown, path: string): void {
  const o = c.obj(v, path);
  if (!o) return;
  c.only(o, ["grade", "seed"], path);
  c.num(o, "grade", path, HILL_GRADES);
  c.num(o, "seed", path, { min: 0, max: 2 ** 32 - 1, optional: true });
  if (typeof o.seed === "number" && !Number.isInteger(o.seed)) c.fail(`${path}.seed`, "expected a whole number");
}

/** A hill's obstacles (#194): the road between the walls, from the spawn on down forever (#195). */
function hillRoad(file: MapFile, seed: number): HillRoad {
  const hx = file.extent?.x ?? file.bounds;
  const hz = file.extent?.z ?? file.bounds;
  return new HillRoad(seed, hx, file.spawn.z, hz - HILL_FINISH);
}

/** Things in a valid map file that are ignored, one line each (a dropped file shows them). */
export function mapFileWarnings(file: MapFile): string[] {
  return file.statPoints === undefined ? [] : ["map.statPoints: ignored, stat points only come from hub challenges now (#86)"];
}

/** How far out a placed piece may stand along x and z: to the fence, or past a soft side over its sand and sea (a pier, #221). */

function pieceReach(bounds: number, edges: unknown): PieceReach {
  const soft = (side: EdgeSide) => Array.isArray(edges) && edges.some((e) => (e as { side?: unknown } | null)?.side === side);
  const out = (side: EdgeSide) => bounds + (soft(side) ? EDGE_FAR : 0);
  return { x: { min: -out("west"), max: out("east") }, z: { min: -out("north"), max: out("south") } };
}

function checkPiece(c: Checker, v: unknown, path: string, bounds: number, reach: PieceReach): void {
  const o = c.obj(v, path);
  if (!o) return;
  const def = Object.hasOwn(PIECES, o.kind as string) ? PIECES[o.kind as MapPiece["kind"]] : undefined;
  if (!def) {
    c.fail(`${path}.kind`, `expected one of ${Object.keys(PIECES).join(", ")}`);
    return;
  }
  c.only(o, ["kind", ...def.fields], path);
  if (def.placed) {
    c.num(o, "x", path, reach.x);
    c.num(o, "z", path, reach.z);
    c.num(o, "rot", path, { min: -360, max: 360, optional: true });
    c.num(o, "hw", path, { min: 0.05, max: 2 * bounds });
  }
  def.check(c, o, path, bounds, reach);
}

/** Checks an untrusted map file (parsed JSON). Throws `MapFileError` listing every problem. */
export function parseMapFile(json: unknown): MapFile {
  const c = new Checker();
  const o = c.obj(json, "map");
  if (!o) throw new MapFileError(c.problems);
  c.only(o, ["version", "id", "name", "blurb", "bounds", "extent", "spawn", "look", "pieces", "gaps", "goals", "challenges", "hubs", "edges", "vehicles", "targets", "graffiti", "props", "hill", "statPoints"], "map");
  if (o.version !== MAP_FILE_VERSION) c.fail("map.version", `expected ${MAP_FILE_VERSION}`);
  c.str(o, "id", "map", ID);
  c.str(o, "name", "map");
  c.str(o, "blurb", "map");
  const bounds = c.num(o, "bounds", "map", { min: MIN_BOUNDS, max: MAX_BOUNDS });
  const spawn = c.obj(o.spawn, "map.spawn");
  if (spawn) {
    c.only(spawn, ["x", "z", "yaw"], "map.spawn");
    const inside = { min: -bounds + 1, max: bounds - 1 };
    c.num(spawn, "x", "map.spawn", inside);
    c.num(spawn, "z", "map.spawn", inside);
    c.num(spawn, "yaw", "map.spawn", { min: -360, max: 360 });
  }
  checkLook(c, o.look, "map.look");
  const reach = pieceReach(bounds, o.edges);
  // A hill (#211) may have none: its road brings its own ramps and roadside.
  if (!Array.isArray(o.pieces) || (o.pieces.length === 0 && o.hill === undefined)) c.fail("map.pieces", "expected a non-empty list");
  else o.pieces.forEach((p, i) => checkPiece(c, p, `map.pieces[${i}]`, bounds, reach));
  const before = c.problems.length;
  checkGaps(c, o.gaps, "map.gaps", bounds, reach);
  checkGoals(c, o.goals, "map.goals", o.gaps, bounds);
  // The ladder is checked against the goals (and their gaps), so only once they're sound.
  if (o.challenges !== undefined && c.problems.length === before) checkChallenges(c, o.challenges, "map.challenges", o.goals, Array.isArray(o.targets) ? o.targets.length : 0, Array.isArray(o.graffiti) ? o.graffiti.length : 0);
  if (o.hubs !== undefined) checkHubs(c, o.hubs, "map.hubs", bounds);
  if (o.edges !== undefined) checkEdges(c, o.edges, "map.edges", bounds);
  if (o.vehicles !== undefined) checkVehicles(c, o.vehicles, "map.vehicles", bounds);
  if (o.targets !== undefined) checkTargets(c, o.targets, "map.targets", bounds);
  if (o.graffiti !== undefined) checkGraffiti(c, o.graffiti, "map.graffiti", bounds);
  if (o.props !== undefined) checkProps(c, o.props, "map.props", bounds);
  if (o.hill !== undefined) checkHill(c, o.hill, "map.hill");
  if (o.extent !== undefined && !c.problems.length) checkExtent(c, o, bounds);
  if ((o.look as Obj | undefined)?.streetEnds !== undefined && !c.problems.length) checkStreetEnds(c, o, bounds);
  if ((o.look as Obj | undefined)?.shopRows !== undefined && !c.problems.length) checkShopRows(c, o, bounds);
  if (c.problems.length) throw new MapFileError(c.problems);
  return json as MapFile;
}

/**
 * Reads a map file's text (a dropped or pasted file): the checked file, or
 * every problem with it, JSON syntax errors included.
 */
export function readMapText(text: string): { file: MapFile } | { problems: string[] } {
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch (e) {
    return { problems: [`not JSON: ${e instanceof Error ? e.message : String(e)}`] };
  }
  try {
    return { file: parseMapFile(json) };
  } catch (e) {
    if (e instanceof MapFileError) return { problems: e.problems };
    throw e;
  }
}

/**
 * Compiles a checked map file into the sim's `Level`. Pieces become
 * heightfield primitives in file order; rails (freestanding, ledges, coping)
 * are collected in the same order.
 */
export function buildLevel(file: MapFile): Level {
  const out: Compiled = { prims: [], rails: [], scenery: [] };
  for (const p of file.pieces) pieceKind(p).compile(p, out);
  const { prims, rails } = out;
  const { x, z, yaw } = file.spawn;
  const level: Level = { bounds: file.bounds, prims, rails, spawn: { x, z, yaw: rad(yaw) } };
  if (out.scenery.length) level.scenery = out.scenery;
  if (file.gaps?.length) level.gaps = file.gaps.map((g) => ({ ...g, from: { ...g.from }, to: { ...g.to } }));
  if (file.edges?.length) level.edges = file.edges.map((e) => ({ ...e }));
  if (file.vehicles?.length) level.vehicles = file.vehicles.map(buildVehicle);
  if (file.extent) level.extent = { ...file.extent };
  if (file.targets?.length) level.targets = file.targets.map((t) => buildTarget(t, level));
  if (file.graffiti?.length) level.graffiti = file.graffiti.map((t) => buildTag(t, level));
  if (file.props?.length) level.props = file.props.map((p) => ({ ...p }));
  if (file.hill) {
    // A hill's obstacles (#194) are laid out from its seed; its ramps are kickers in the park.
    level.hill = { grade: file.hill.grade, parkPrims: prims, parkRails: rails };
    if (file.hill.seed !== undefined) {
      level.hill.road = hillRoad(file, file.hill.seed);
      placeRoad(level);
    }
  }
  return level;
}
